import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { 
  FileText, Plus, Search, Trash2, Edit, X, Calculator 
} from 'lucide-react';
import LoadingSpinner from '../../ui/LoadingSpinner';
import {
  getCustomsDeclarations,
  createCustomsDeclaration,
  updateCustomsDeclaration,
  deleteCustomsDeclaration,
  getCustomsDeclaration
} from '../../../redux/api';
import { su } from '../../inventory/stockUi';

const CustomsView = () => {
  const { t } = useTranslation();
  const [declarations, setDeclarations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingDeclaration, setEditingDeclaration] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState({ startDate: '', endDate: '' });
  const [statusFilter, setStatusFilter] = useState('all');
  const [formData, setFormData] = useState({
    declarationNumber: '',
    declarationDate: new Date().toISOString().split('T')[0],
    customsOffice: '',
    totalInvoiceValue: 0,
    currency: 'EUR',
    exchangeRate: 1,
    freightCost: 0,
    insuranceCost: 0,
    otherCosts: 0,
    customsDutyRate: 0,
    vatRate: 20,
    supplierName: '',
    supplierAddress: '',
    supplierCountry: '',
    importerName: '',
    importerAddress: '',
    importerPIB: '',
    invoiceNumber: '',
    invoiceDate: '',
    stockSource: 'BOTH',
    notes: ''
  });

  useEffect(() => {
    fetchDeclarations();
  }, [dateFilter, statusFilter]);

  const fetchDeclarations = async () => {
    try {
      setLoading(true);
      const params = {};
      if (dateFilter.startDate) params.startDate = dateFilter.startDate;
      if (dateFilter.endDate) params.endDate = dateFilter.endDate;
      if (statusFilter !== 'all') params.status = statusFilter;

      const response = await getCustomsDeclarations(params);
      setDeclarations(response.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Greška pri učitavanju uvoznih obračuna');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    const numValue = type === 'number' ? parseFloat(value) || 0 : value;
    setFormData(prev => ({
      ...prev,
      [name]: numValue
    }));
  };

  const calculateTotals = () => {
    const {
      totalInvoiceValue,
      exchangeRate,
      freightCost,
      insuranceCost,
      otherCosts,
      customsDutyRate,
      vatRate
    } = formData;

    const totalLandedCost = totalInvoiceValue + freightCost + insuranceCost + otherCosts;
    const customsBasis = totalLandedCost * exchangeRate;
    const customsDuty = customsBasis * (customsDutyRate / 100);
    const vatBasis = customsBasis + customsDuty;
    const vatAmount = vatBasis * (vatRate / 100);
    const totalAmount = customsDuty + vatAmount;

    return {
      totalLandedCost,
      customsBasis,
      customsDuty,
      vatBasis,
      vatAmount,
      totalAmount
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const totals = calculateTotals();
      const dataToSubmit = {
        ...formData,
        ...totals
      };

      if (editingDeclaration) {
        await updateCustomsDeclaration({ id: editingDeclaration._id, declarationData: dataToSubmit });
        toast.success('Uvozni obračun je ažuriran');
      } else {
        await createCustomsDeclaration(dataToSubmit);
        toast.success('Uvozni obračun je kreiran');
      }
      setShowForm(false);
      setEditingDeclaration(null);
      resetForm();
      fetchDeclarations();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Greška pri čuvanju uvoznog obračuna');
    }
  };

  const resetForm = () => {
    setFormData({
      declarationNumber: '',
      declarationDate: new Date().toISOString().split('T')[0],
      customsOffice: '',
      totalInvoiceValue: 0,
      currency: 'EUR',
      exchangeRate: 1,
      freightCost: 0,
      insuranceCost: 0,
      otherCosts: 0,
      customsDutyRate: 0,
      vatRate: 20,
      supplierName: '',
      supplierAddress: '',
      supplierCountry: '',
      importerName: '',
      importerAddress: '',
      importerPIB: '',
      invoiceNumber: '',
      invoiceDate: '',
      notes: ''
    });
  };

  const handleEdit = async (id) => {
    try {
      const response = await getCustomsDeclaration(id);
      const declaration = response.data;
      setEditingDeclaration(declaration);
      setFormData({
        declarationNumber: declaration.declarationNumber,
        declarationDate: new Date(declaration.declarationDate).toISOString().split('T')[0],
        customsOffice: declaration.customsOffice || '',
        totalInvoiceValue: declaration.totalInvoiceValue,
        currency: declaration.currency || 'EUR',
        exchangeRate: declaration.exchangeRate || 1,
        freightCost: declaration.freightCost || 0,
        insuranceCost: declaration.insuranceCost || 0,
        otherCosts: declaration.otherCosts || 0,
        customsDutyRate: declaration.customsDutyRate || 0,
        vatRate: declaration.vatRate || 20,
        supplierName: declaration.supplierName || '',
        supplierAddress: declaration.supplierAddress || '',
        supplierCountry: declaration.supplierCountry || '',
        importerName: declaration.importerName || '',
        importerAddress: declaration.importerAddress || '',
        importerPIB: declaration.importerPIB || '',
        invoiceNumber: declaration.invoiceNumber || '',
        invoiceDate: declaration.invoiceDate ? new Date(declaration.invoiceDate).toISOString().split('T')[0] : '',
        stockSource: declaration.stockSource || 'BOTH',
        notes: declaration.notes || ''
      });
      setShowForm(true);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Greška pri učitavanju uvoznog obračuna');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Da li ste sigurni da želite da obrišete ovaj uvozni obračun?')) {
      return;
    }
    try {
      await deleteCustomsDeclaration(id);
      toast.success('Uvozni obračun je obrisan');
      fetchDeclarations();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Greška pri brisanju uvoznog obračuna');
    }
  };

  const filteredDeclarations = declarations.filter(d =>
    d.declarationNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.supplierName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.importerName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totals = calculateTotals();

  if (loading) {
    return (
      <div className={`${su.page} flex items-center justify-center`}>
        <LoadingSpinner size="lg" color="white" />
      </div>
    );
  }

  return (
    <div className={su.page}>
      <div className={su.wrap}>
        <div className={`${su.card} ${su.cardPad}`}>
          <div className={su.toolbar}>
            <div className="min-w-0">
              <h1 className={su.title}>
                <FileText className={su.icon} />
                {t('inventory.customs.title', 'Uvoz')}
              </h1>
              <p className={su.subtitle}>
                {t('inventory.customs.subtitle', 'Evidencija uvoza i obračun troškova')}
              </p>
            </div>
            <div className={su.searchWrap}>
              <Search className={su.searchIcon} />
              <input
                type="text"
                placeholder={t('inventory.customs.search', 'Pretraži...')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={su.searchInput}
              />
            </div>
            <input
              type="date"
              title={t('common.startDate', 'Datum od')}
              value={dateFilter.startDate}
              onChange={(e) => setDateFilter((prev) => ({ ...prev, startDate: e.target.value }))}
              className={`${su.input} ${su.date}`}
              style={{ colorScheme: 'dark' }}
            />
            <input
              type="date"
              title={t('common.endDate', 'Datum do')}
              value={dateFilter.endDate}
              onChange={(e) => setDateFilter((prev) => ({ ...prev, endDate: e.target.value }))}
              className={`${su.input} ${su.date}`}
              style={{ colorScheme: 'dark' }}
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={`${su.input} w-36 shrink-0`}
            >
              <option value="all">{t('jobs.all', 'Svi')}</option>
              <option value="draft">Nacrt</option>
              <option value="submitted">Podneto</option>
              <option value="cleared">Prošlo carinu</option>
            </select>
            <button
              onClick={() => {
                setEditingDeclaration(null);
                resetForm();
                setShowForm(true);
              }}
              className={su.btnPrimary}
            >
              <Plus className="h-3.5 w-3.5" />
              {t('inventory.customs.addDeclaration', 'Dodaj uvoz')}
            </button>
          </div>
        </div>

        {/* Form */}
        {showForm && (
          <div className="bg-gray-800/90 rounded-lg border border-gray-700/80 p-3">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-semibold text-white">
                {editingDeclaration ? t('inventory.customs.editDeclaration', 'Izmeni uvozni obračun') : t('inventory.customs.newDeclaration', 'Novi uvozni obračun')}
              </h2>
              <button
                onClick={() => {
                  setShowForm(false);
                  setEditingDeclaration(null);
                }}
                className="text-gray-400 hover:text-white"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-2">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                <div>
                  <label className="block text-[11px] font-medium text-gray-400 mb-0.5">
                    {t('inventory.customs.declarationNumber', 'Broj deklaracije')} *
                  </label>
                  <input
                    type="text"
                    name="declarationNumber"
                    value={formData.declarationNumber}
                    onChange={handleInputChange}
                    required
                    className="w-full h-8 px-2.5 text-xs bg-gray-900/70 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-400 mb-0.5">
                    {t('inventory.customs.declarationDate', 'Datum uvoza')} *
                  </label>
                  <input
                    type="date"
                    name="declarationDate"
                    value={formData.declarationDate}
                    onChange={handleInputChange}
                    required
                    className="w-full h-8 px-2.5 text-xs bg-gray-900/70 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-400 mb-0.5">
                    {t('inventory.customs.stockSource', 'Skladište')}
                  </label>
                  <select
                    name="stockSource"
                    value={formData.stockSource}
                    onChange={handleInputChange}
                    className="w-full h-8 px-2.5 text-xs bg-gray-900/70 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="BOTH">Magacin i rezervni delovi</option>
                    <option value="INVENTORY">Samo magacin</option>
                    <option value="SPARE_PART">Samo rezervni delovi</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-400 mb-0.5">
                    {t('inventory.customs.totalInvoiceValue', 'Fakturisana vrednost')} *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="totalInvoiceValue"
                    value={formData.totalInvoiceValue}
                    onChange={handleInputChange}
                    required
                    className="w-full h-8 px-2.5 text-xs bg-gray-900/70 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-400 mb-0.5">
                    {t('inventory.customs.currency', 'Valuta')}
                  </label>
                  <select
                    name="currency"
                    value={formData.currency}
                    onChange={handleInputChange}
                    className="w-full h-8 px-2.5 text-xs bg-gray-900/70 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="EUR">EUR</option>
                    <option value="USD">USD</option>
                    <option value="RSD">RSD</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-400 mb-0.5">
                    {t('inventory.customs.exchangeRate', 'Kurs')}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="exchangeRate"
                    value={formData.exchangeRate}
                    onChange={handleInputChange}
                    className="w-full h-8 px-2.5 text-xs bg-gray-900/70 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-400 mb-0.5">
                    {t('inventory.customs.customsDutyRate', 'Carinska stopa (%)')}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="customsDutyRate"
                    value={formData.customsDutyRate}
                    onChange={handleInputChange}
                    className="w-full h-8 px-2.5 text-xs bg-gray-900/70 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-400 mb-0.5">
                    {t('inventory.customs.vatRate', 'PDV stopa (%)')}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="vatRate"
                    value={formData.vatRate}
                    onChange={handleInputChange}
                    className="w-full h-8 px-2.5 text-xs bg-gray-900/70 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-400 mb-0.5">
                    {t('inventory.customs.customsOffice', 'Carinarnica')}
                  </label>
                  <input
                    type="text"
                    name="customsOffice"
                    value={formData.customsOffice}
                    onChange={handleInputChange}
                    className="w-full h-8 px-2.5 text-xs bg-gray-900/70 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-400 mb-0.5">
                    {t('inventory.customs.freightCost', 'Troškovi prevoza')}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="freightCost"
                    value={formData.freightCost}
                    onChange={handleInputChange}
                    className="w-full h-8 px-2.5 text-xs bg-gray-900/70 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-400 mb-0.5">
                    {t('inventory.customs.insuranceCost', 'Osiguranje')}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="insuranceCost"
                    value={formData.insuranceCost}
                    onChange={handleInputChange}
                    className="w-full h-8 px-2.5 text-xs bg-gray-900/70 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-400 mb-0.5">
                    {t('inventory.customs.otherCosts', 'Ostali troškovi')}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="otherCosts"
                    value={formData.otherCosts}
                    onChange={handleInputChange}
                    className="w-full h-8 px-2.5 text-xs bg-gray-900/70 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-400 mb-0.5">
                    {t('inventory.customs.supplierName', 'Dobavljač')}
                  </label>
                  <input
                    type="text"
                    name="supplierName"
                    value={formData.supplierName}
                    onChange={handleInputChange}
                    className="w-full h-8 px-2.5 text-xs bg-gray-900/70 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-400 mb-0.5">
                    {t('inventory.customs.importerName', 'Uvoznik')}
                  </label>
                  <input
                    type="text"
                    name="importerName"
                    value={formData.importerName}
                    onChange={handleInputChange}
                    className="w-full h-8 px-2.5 text-xs bg-gray-900/70 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-400 mb-0.5">
                    {t('inventory.customs.invoiceNumber', 'Broj fakture')}
                  </label>
                  <input
                    type="text"
                    name="invoiceNumber"
                    value={formData.invoiceNumber}
                    onChange={handleInputChange}
                    className="w-full h-8 px-2.5 text-xs bg-gray-900/70 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-400 mb-0.5">
                    {t('inventory.customs.invoiceDate', 'Datum fakture')}
                  </label>
                  <input
                    type="date"
                    name="invoiceDate"
                    value={formData.invoiceDate}
                    onChange={handleInputChange}
                    className="w-full h-8 px-2.5 text-xs bg-gray-900/70 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-medium text-gray-400 mb-0.5">
                  {t('common.notes', 'Napomene')}
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full h-8 px-2.5 text-xs bg-gray-900/70 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              {/* Calculated Totals */}
              <div className="bg-gray-900/50 rounded-md px-2.5 py-2">
                <div className="flex items-center gap-2 mb-3">
                  <Calculator className="h-5 w-5 text-blue-400" />
                  <h3 className="font-medium text-white">{t('inventory.customs.calculations', 'Kalkulacije')}</h3>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                  <div>
                    <div className="text-gray-400">{t('inventory.customs.customsDuty', 'Carina')}</div>
                    <div className="text-white font-medium">{new Intl.NumberFormat('sr-RS', { style: 'currency', currency: 'RSD' }).format(totals.customsDuty || 0)}</div>
                  </div>
                  <div>
                    <div className="text-gray-400">{t('inventory.customs.vatAmount', 'PDV')}</div>
                    <div className="text-white font-medium">{new Intl.NumberFormat('sr-RS', { style: 'currency', currency: 'RSD' }).format(totals.vatAmount || 0)}</div>
                  </div>
                  <div>
                    <div className="text-gray-400">{t('inventory.customs.totalAmount', 'Ukupno')}</div>
                    <div className="text-white font-bold text-lg">{new Intl.NumberFormat('sr-RS', { style: 'currency', currency: 'RSD' }).format(totals.totalAmount || 0)}</div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingDeclaration(null);
                  }}
                  className="inline-flex items-center h-8 px-3 text-xs font-medium bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-md transition"
                >
                  {t('common.cancel', 'Otkaži')}
                </button>
                <button
                  type="submit"
                  className="inline-flex items-center h-8 px-3 text-xs font-medium bg-blue-600 hover:bg-blue-500 text-white rounded-md transition"
                >
                  {editingDeclaration ? t('common.save', 'Sačuvaj') : t('common.create', 'Kreiraj')}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Declarations List */}
        <div className="bg-gray-800/90 rounded-lg border border-gray-700/80 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={su.tableHead}>
                <tr>
                  <th className="px-2.5 py-1.5 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wide">
                    {t('inventory.customs.declarationNumber', 'Broj')}
                  </th>
                  <th className="px-2.5 py-1.5 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wide">
                    {t('inventory.customs.declarationDate', 'Datum')}
                  </th>
                  <th className="px-2.5 py-1.5 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wide">
                    {t('inventory.customs.totalInvoiceValue', 'Vrednost')}
                  </th>
                  <th className="px-2.5 py-1.5 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wide">
                    {t('inventory.customs.customsDuty', 'Carina')}
                  </th>
                  <th className="px-2.5 py-1.5 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wide">
                    {t('inventory.customs.vatAmount', 'PDV')}
                  </th>
                  <th className="px-2.5 py-1.5 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wide">
                    {t('inventory.customs.totalAmount', 'Ukupno')}
                  </th>
                  <th className="px-2.5 py-1.5 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wide">
                    {t('common.actions', 'Akcije')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {filteredDeclarations.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-3 py-6 text-center text-xs text-gray-500">
                      {t('inventory.customs.noDeclarations', 'Nema uvoznih obračuna')}
                    </td>
                  </tr>
                ) : (
                  filteredDeclarations.map((declaration) => (
                    <tr key={declaration._id} className="hover:bg-gray-700">
                      <td className="px-2.5 py-1.5 text-xs text-white">{declaration.declarationNumber}</td>
                      <td className="px-2.5 py-1.5 text-xs text-white">
                        {new Date(declaration.declarationDate).toLocaleDateString('sr-RS')}
                      </td>
                      <td className="px-2.5 py-1.5 text-xs text-white">
                        {new Intl.NumberFormat('sr-RS', { style: 'currency', currency: declaration.currency || 'EUR' }).format(declaration.totalInvoiceValue || 0)}
                      </td>
                      <td className="px-2.5 py-1.5 text-xs text-white">
                        {new Intl.NumberFormat('sr-RS', { style: 'currency', currency: 'RSD' }).format(declaration.customsDuty || 0)}
                      </td>
                      <td className="px-2.5 py-1.5 text-xs text-white">
                        {new Intl.NumberFormat('sr-RS', { style: 'currency', currency: 'RSD' }).format(declaration.vatAmount || 0)}
                      </td>
                      <td className="px-2.5 py-1.5 text-xs text-white font-bold">
                        {new Intl.NumberFormat('sr-RS', { style: 'currency', currency: 'RSD' }).format(declaration.totalAmount || 0)}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(declaration._id)}
                            className="text-blue-400 hover:text-blue-300"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(declaration._id)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomsView;

